import type { CardArtwork } from "../schemas"
import { ARTWORK_VARIANT_BOUNDS, validateArtworkUploadInput } from "./artwork"
import type { ArtworkAssetStore } from "./assets"
import { ArtworkError } from "./errors"

type LoadedImage = {
  bitmap: ImageBitmap | null
  image: HTMLImageElement | null
  width: number
  height: number
}

export async function processArtworkFile(file: File): Promise<{
  artwork: CardArtwork
  assets: ArtworkAssetStore
}> {
  const loaded = await loadImage(file)
  const issues = validateArtworkUploadInput({
    mimeType: file.type,
    extension: getFileExtension(file.name),
    byteLength: file.size,
    width: loaded.width,
    height: loaded.height,
  })
  if (issues.length > 0) {
    cleanupLoadedImage(loaded)
    throw new ArtworkError(issues[0]?.code ?? "ARTWORK_INVALID_UPLOAD")
  }

  try {
    const createdAt = new Date().toISOString()
    const variants = await Promise.all(
      (["card_art", "thumb"] as const).map(async (kind) => {
        const bounds = ARTWORK_VARIANT_BOUNDS[kind]
        const { width, height } = fitWithinBounds(
          loaded.width,
          loaded.height,
          bounds.width,
          bounds.height,
        )
        const blob = await renderVariantToWebp(loaded, width, height)
        const dataUrl = await blobToDataUrl(blob)
        const assetId = createArtworkAssetId(kind)

        return {
          asset: {
            assetId,
            kind,
            mimeType: "image/webp" as const,
            width,
            height,
            byteLength: blob.size,
            dataUrl,
            createdAt,
          },
          variant: {
            assetId,
            kind,
            mimeType: "image/webp" as const,
            width,
            height,
            byteLength: blob.size,
          },
          blob,
        }
      }),
    )

    const cardArtBlob =
      variants.find((entry) => entry.variant.kind === "card_art")?.blob ??
      variants[0]?.blob
    if (!cardArtBlob) {
      throw new ArtworkError("ARTWORK_PROCESSING_NO_VARIANTS")
    }

    return {
      artwork: {
        hash: await hashBlob(cardArtBlob),
        focalPoint: {
          x: 0.5,
          y: 0.5,
        },
        zoom: 1,
        variants: variants.map((entry) => entry.variant),
      },
      assets: variants.map((entry) => entry.asset),
    }
  } finally {
    cleanupLoadedImage(loaded)
  }
}

export function fitWithinBounds(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  const scale = Math.min(maxWidth / width, maxHeight / height, 1)
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

async function loadImage(file: File): Promise<LoadedImage> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file)
      return {
        bitmap,
        image: null,
        width: bitmap.width,
        height: bitmap.height,
      }
    } catch {
      // Fall back to HTMLImageElement decoding below. Some browsers expose
      // createImageBitmap but still cannot decode HEIC/HEIF there reliably.
    }
  }

  const objectUrl = URL.createObjectURL(file)
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image()
      nextImage.onload = () => resolve(nextImage)
      nextImage.onerror = () =>
        reject(new ArtworkError("ARTWORK_DECODE_FAILED"))
      nextImage.src = objectUrl
    })
    return {
      bitmap: null,
      image,
      width: image.naturalWidth,
      height: image.naturalHeight,
    }
  } catch {
    if (file.type === "image/heic" || file.type === "image/heif") {
      throw new ArtworkError("ARTWORK_HEIC_UNSUPPORTED")
    }
    throw new ArtworkError("ARTWORK_DECODE_FAILED")
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

function cleanupLoadedImage(loaded: LoadedImage) {
  loaded.bitmap?.close()
}

async function renderVariantToWebp(
  loaded: LoadedImage,
  width: number,
  height: number,
): Promise<Blob> {
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d")
  if (!context) {
    throw new ArtworkError("ARTWORK_CANVAS_UNAVAILABLE")
  }

  context.clearRect(0, 0, width, height)
  if (loaded.bitmap) {
    context.drawImage(loaded.bitmap, 0, 0, width, height)
  } else if (loaded.image) {
    context.drawImage(loaded.image, 0, 0, width, height)
  } else {
    throw new ArtworkError("ARTWORK_SOURCE_UNAVAILABLE")
  }

  const blob = await canvasToBlob(canvas, "image/webp", 0.9)
  return blob
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new ArtworkError("ARTWORK_ENCODE_FAILED"))
      },
      mimeType,
      quality,
    )
  })
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new ArtworkError("ARTWORK_READ_FAILED"))
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new ArtworkError("ARTWORK_DATA_URL_FAILED"))
        return
      }
      resolve(reader.result)
    }
    reader.readAsDataURL(blob)
  })
}

async function hashBlob(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer()
  const digest = await crypto.subtle.digest("SHA-256", buffer)
  return [...new Uint8Array(digest)]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")
}

function getFileExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf(".")
  return dotIndex >= 0 ? filename.slice(dotIndex).toLowerCase() : ""
}

function createArtworkAssetId(kind: "card_art" | "thumb"): string {
  if (typeof crypto.randomUUID === "function") {
    return `art_${kind}_${crypto.randomUUID().replace(/-/gu, "")}`
  }

  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  const opaqueId = [...bytes]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")
  return `art_${kind}_${opaqueId}`
}
